import { useEffect, useState } from "react";
import { api, ApiError, type AdminUser, type Device } from "../api/client";

// Per-user device approval (spec §7.4): approve / revoke / rename / delete the
// browsers a student may sign in from.

export default function DeviceManager({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function refresh() {
    setError(null);
    api
      .adminListDevices(user.id)
      .then(setDevices)
      .catch((e) => setError(e instanceof ApiError ? e.message : "Could not load devices."));
  }

  useEffect(refresh, [user.id]);

  async function setApproved(d: Device, approved: boolean) {
    try {
      await api.adminUpdateDevice(d.id, { is_approved: approved });
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Update failed.");
    }
  }

  async function rename(d: Device) {
    const label = window.prompt("Device label (e.g. 'Lab PC 3'):", d.label);
    if (label === null) return;
    try {
      await api.adminUpdateDevice(d.id, { label });
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Rename failed.");
    }
  }

  async function remove(d: Device) {
    if (!window.confirm("Delete this device? The student must re-register it next login.")) return;
    try {
      await api.adminDeleteDevice(d.id);
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Delete failed.");
    }
  }

  async function resetAll() {
    if (
      !window.confirm(
        `Reset all devices for "${user.username}"? They will re-register and need approval on next login.`,
      )
    )
      return;
    try {
      await api.adminResetDevices(user.id);
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Reset failed.");
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-700 bg-slate-800 px-4 py-3">
          <h2 className="font-semibold text-slate-100">
            Devices — <span className="font-mono">{user.username}</span>
          </h2>
          <div className="flex items-center gap-2">
            {devices && devices.length > 0 && (
              <button
                type="button"
                onClick={resetAll}
                className="rounded bg-amber-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-600"
              >
                Reset all devices
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded bg-slate-700 px-3 py-1.5 text-sm font-medium text-slate-100 hover:bg-slate-600"
            >
              Close
            </button>
          </div>
        </div>
        {error && <div className="bg-red-950/50 px-4 py-2 text-sm text-red-300">{error}</div>}
        <div className="overflow-auto p-4 text-sm">
          {devices === null ? (
            <p className="text-slate-400">Loading…</p>
          ) : devices.length === 0 ? (
            <p className="text-slate-400">
              No devices registered. A device appears here the first time this student tries to sign
              in (it starts as “pending” and you approve it).
            </p>
          ) : (
            <table className="w-full text-left">
              <thead className="text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-2 py-1">Label</th>
                  <th className="px-2 py-1">Token</th>
                  <th className="px-2 py-1">Status</th>
                  <th className="px-2 py-1">Last seen</th>
                  <th className="px-2 py-1">Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id} className="border-t border-slate-800">
                    <td className="px-2 py-1">
                      {d.label || <span className="text-slate-500">—</span>}
                    </td>
                    <td className="px-2 py-1 font-mono text-xs text-slate-400" title={d.token}>
                      {d.token.slice(0, 8)}…
                    </td>
                    <td className="px-2 py-1">
                      {d.is_approved ? (
                        <span className="text-emerald-400">approved</span>
                      ) : (
                        <span className="text-amber-400">pending</span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-xs text-slate-400">
                      {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : "never"}
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex flex-wrap gap-1">
                        {d.is_approved ? (
                          <button
                            type="button"
                            className="rounded px-2 py-0.5 text-xs text-amber-300 hover:bg-slate-800"
                            onClick={() => setApproved(d, false)}
                          >
                            Revoke
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="rounded bg-emerald-700 px-2 py-0.5 text-xs text-white hover:bg-emerald-600"
                            onClick={() => setApproved(d, true)}
                          >
                            Approve
                          </button>
                        )}
                        <button
                          type="button"
                          className="rounded px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-800"
                          onClick={() => rename(d)}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="rounded px-2 py-0.5 text-xs text-red-400 hover:bg-red-950/60"
                          onClick={() => remove(d)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
